import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaUsers,
  FaUserCheck,
  FaUserSlash,
  FaUserPlus,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaFileCsv,
  FaBan,
  FaUnlock,
  FaTrash,
  FaExternalLinkAlt,
  FaCopy,
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaInbox,
  FaCoins,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes ulFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes ulFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ulSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes ulZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
.ul-in { animation: ulFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.ul-fade { animation: ulFade .2s ease both; }
.ul-slide { animation: ulSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.ul-zoom { animation: ulZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.ul-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.ul-press:hover { transform: translateY(-1px); }
.ul-press:active { transform: scale(.96); }
.ul-row { transition: background-color .15s ease; cursor: pointer; }
.ul-card { transition: transform .2s ease, box-shadow .25s ease; }
.ul-card:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -14px rgba(15,23,42,.3); }
@media (prefers-reduced-motion: reduce) {
  .ul-in, .ul-fade, .ul-slide, .ul-zoom { animation: none; }
  .ul-press:hover, .ul-press:active, .ul-card:hover { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const DAY = 24 * 60 * 60 * 1000;

const joinedAt = (u) => {
  if (!u?.reg_date) return null;
  const d = new Date(`${u.reg_date}T${u.reg_time || "00:00:00"}`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const isBlocked = (u) => String(u?.status || "").toLowerCase() === "block";
const isActive = (u) => String(u?.status || "").toLowerCase() === "live";

const waLink = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
};

const Avatar = ({ src, name, size = 38 }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err)
    return (
      <img
        src={src}
        alt=""
        onError={() => setErr(true)}
        className="rounded-full object-cover shrink-0 bg-slate-100"
        style={{ width: size, height: size }}
      />
    );
  return (
    <span
      className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </span>
  );
};

const StatusBadge = ({ user }) => {
  if (isBlocked(user))
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Blocked
      </span>
    );
  if (isActive(user))
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
      </span>
    );
  return (
    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
      {user?.status || "Unknown"}
    </span>
  );
};

/* ================= page ================= */

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [joinedFilter, setJoinedFilter] = useState("all");
  const [sort, setSort] = useState({ field: "joined", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState([]);

  const [drawerUser, setDrawerUser] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type:'block'|'unblock'|'delete'|'bulk-block'|'bulk-unblock', users:[...]}
  const [deleteText, setDeleteText] = useState("");
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same as before) ----- */

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "all-users");
      const res = await fetch(`${baseUrl}/users.php`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) && data?.error) {
        toast.current?.show({ severity: "error", summary: "Error", detail: data.error });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch users" });
    }
    setLoading(false);
  }, [baseUrl]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const setStatus = async (user_id, status) => {
    const formData = new FormData();
    formData.append("action", "block-user");
    formData.append("user_id", user_id);
    formData.append("status", status);
    const res = await fetch(`${baseUrl}/users.php`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok || (data && data.status === false)) throw new Error(data?.error || "Request failed");
    return data;
  };

  /* ----- actions ----- */

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, users: targets } = confirm;
    const status = type.includes("unblock") ? "Live" : type === "delete" ? "Delete" : "Block";
    setWorking(true);
    let ok = 0;
    for (const u of targets) {
      try {
        await setStatus(u.user_id, status);
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setDeleteText("");
    setSelectedIds([]);

    const verb = status === "Live" ? "unblocked" : status === "Block" ? "blocked" : "deleted";
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: "Updated",
        detail: ok === 1 ? `User ${verb} successfully` : `${ok} users ${verb} successfully`,
      });
    }
    if (ok < targets.length) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `${targets.length - ok} user(s) could not be ${verb}`,
      });
    }
    if (status === "Delete") setDrawerUser(null);
    fetchUsers();
  };

  const askToggle = (u) => setConfirm({ type: isBlocked(u) ? "unblock" : "block", users: [u] });
  const askDelete = (u) => {
    setDeleteText("");
    setConfirm({ type: "delete", users: [u] });
  };

  /* ----- derived ----- */

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      total: users.length,
      active: users.filter(isActive).length,
      blocked: users.filter(isBlocked).length,
      week: users.filter((u) => {
        const d = joinedAt(u);
        return d && now - d.getTime() <= 7 * DAY;
      }).length,
    };
  }, [users]);

  const genders = useMemo(() => {
    const set = new Set(users.map((u) => u.gender).filter(Boolean));
    return Array.from(set);
  }, [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = Date.now();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    let list = users.filter((u) => {
      if (statusFilter === "active" && !isActive(u)) return false;
      if (statusFilter === "blocked" && !isBlocked(u)) return false;
      if (genderFilter !== "all" && u.gender !== genderFilter) return false;
      if (joinedFilter !== "all") {
        const d = joinedAt(u);
        if (!d) return false;
        if (joinedFilter === "today" && d < startOfToday) return false;
        if (joinedFilter === "7d" && now - d.getTime() > 7 * DAY) return false;
        if (joinedFilter === "30d" && now - d.getTime() > 30 * DAY) return false;
      }
      if (!q) return true;
      return [u.user_id, u.name, u.email_id, u.contact]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q));
    });

    const dir = sort.dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      let x;
      let y;
      if (sort.field === "joined") {
        x = joinedAt(a)?.getTime() || 0;
        y = joinedAt(b)?.getTime() || 0;
      } else if (sort.field === "point") {
        x = Number(a.point || 0);
        y = Number(b.point || 0);
      } else if (sort.field === "user_id") {
        x = Number(a.user_id) || 0;
        y = Number(b.user_id) || 0;
      } else {
        x = String(a[sort.field] || "").toLowerCase();
        y = String(b[sort.field] || "").toLowerCase();
      }
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
    return list;
  }, [users, query, statusFilter, genderFilter, joinedFilter, sort]);

  useEffect(() => setPage(1), [query, statusFilter, genderFilter, joinedFilter, pageSize, sort]);

  useEffect(() => {
    const valid = new Set(users.map((u) => u.user_id));
    setSelectedIds((ids) => ids.filter((id) => valid.has(id)));
    setDrawerUser((d) => (d ? users.find((u) => u.user_id === d.user_id) || null : d));
  }, [users]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pageIds = pageRows.map((u) => u.user_id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const selectedUsers = users.filter((u) => selectedIds.includes(u.user_id));

  const toggleSelect = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const togglePageSelect = () =>
    setSelectedIds((ids) =>
      allPageSelected ? ids.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...ids, ...pageIds]))
    );

  const toggleSort = (field) =>
    setSort((s) => ({ field, dir: s.field === field && s.dir === "desc" ? "asc" : "desc" }));

  const hasFilters = query || statusFilter !== "all" || genderFilter !== "all" || joinedFilter !== "all";
  const clearFilters = () => {
    setQuery("");
    setStatusFilter("all");
    setGenderFilter("all");
    setJoinedFilter("all");
  };

  /* ----- misc ----- */

  const copyText = (text, label = "Copied") => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const exportCsv = (list) => {
    const head = ["User ID", "Name", "Email", "Contact", "Gender", "DOB", "Points", "Status", "Reg. Date", "Reg. Time"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = list.map((u) =>
      [u.user_id, u.name, u.email_id, u.contact, u.gender, u.dob, u.point, u.status, u.reg_date, u.reg_time]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (confirm && !working) setConfirm(null);
      else if (drawerUser) setDrawerUser(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, drawerUser]);

  /* ----- UI bits ----- */

  const statCards = [
    { label: "Total users", value: stats.total, icon: FaUsers, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300", onClick: () => setStatusFilter("all") },
    { label: "Active", value: stats.active, icon: FaUserCheck, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300", onClick: () => setStatusFilter("active") },
    { label: "Blocked", value: stats.blocked, icon: FaUserSlash, tint: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300", onClick: () => setStatusFilter("blocked") },
    { label: "Joined last 7 days", value: stats.week, icon: FaUserPlus, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300", onClick: () => setJoinedFilter("7d") },
  ];

  const SortTh = ({ field, children, right }) => {
    const active = sort.field === field;
    const Icon = !active ? FaSort : sort.dir === "asc" ? FaSortUp : FaSortDown;
    return (
      <th className={`px-3 py-3 font-medium whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        <button
          type="button"
          onClick={() => toggleSort(field)}
          className="inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white"
        >
          {children}
          <Icon size={10} className={active ? "text-cyan-600" : "text-slate-300 dark:text-slate-500"} />
        </button>
      </th>
    );
  };

  const confirmText = () => {
    if (!confirm) return {};
    const n = confirm.users.length;
    const who = n === 1 ? confirm.users[0].name || `user ${confirm.users[0].user_id}` : `${n} users`;
    if (confirm.type.includes("unblock"))
      return {
        title: `Unblock ${who}?`,
        body: "They can use the app again and will get an 'Account Activated' notification.",
        button: "Yes, unblock",
        color: "bg-emerald-600 hover:bg-emerald-700",
        icon: FaUnlock,
      };
    if (confirm.type === "delete")
      return {
        title: `Delete ${who} permanently?`,
        body:
          "This removes the account and all its data: wallet, points, posts, likes, comments, group membership, withdrawals and reports. It cannot be undone.",
        button: "Delete forever",
        color: "bg-rose-600 hover:bg-rose-700",
        icon: FaTrash,
      };
    return {
      title: `Block ${who}?`,
      body: "They will not be able to use the app and will get an 'Account Suspended' notification.",
      button: "Yes, block",
      color: "bg-rose-600 hover:bg-rose-700",
      icon: FaBan,
    };
  };
  const ct = confirmText();
  const deleteReady = confirm?.type !== "delete" || deleteText.trim().toUpperCase() === "DELETE";

  /* ================= render ================= */

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen"
    >
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="ul-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">App Users</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">All Users</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Find any user, see their details, and block, unblock or delete accounts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => exportCsv(filtered)}
            disabled={!filtered.length}
            className="ul-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <FaFileCsv /> Export CSV
          </button>
          <button
            type="button"
            onClick={fetchUsers}
            disabled={loading}
            className="ul-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats (click to filter) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <button
              key={c.label}
              type="button"
              onClick={c.onClick}
              className={`${CARD} ul-card ul-in p-4 flex items-center gap-4 text-left active:scale-[.98]`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                {loading && !users.length ? (
                  <div className="h-6 w-12 mt-1 rounded bg-slate-200 dark:bg-slate-600 animate-pulse" />
                ) : (
                  <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Table card */}
      <div className={`${CARD} ul-in mt-4`} style={{ animationDelay: "240ms" }}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="relative flex-1 min-w-[240px]">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, user ID, email or phone"
              className={`${INPUT} w-full h-10 pl-9 pr-9`}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>

          <div className="inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
            {[
              { id: "all", label: "All" },
              { id: "active", label: "Active" },
              { id: "blocked", label: "Blocked" },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatusFilter(s.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all active:scale-95 ${
                  statusFilter === s.id
                    ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-300 hover:text-slate-800"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} className={`${INPUT} h-10 px-3`}>
            <option value="all">All genders</option>
            {genders.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>

          <select value={joinedFilter} onChange={(e) => setJoinedFilter(e.target.value)} className={`${INPUT} h-10 px-3`}>
            <option value="all">Joined: any time</option>
            <option value="today">Joined today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>

        {/* Filter info / bulk bar */}
        {selectedIds.length > 0 ? (
          <div className="ul-fade flex flex-wrap items-center gap-2 px-4 py-2.5 bg-cyan-50 dark:bg-cyan-900/20 border-b border-cyan-200 dark:border-cyan-800 text-sm">
            <span className="font-medium text-cyan-900 dark:text-cyan-100 mr-2">{selectedIds.length} selected</span>
            <button
              type="button"
              onClick={() => setConfirm({ type: "bulk-block", users: selectedUsers.filter((u) => !isBlocked(u)) })}
              disabled={!selectedUsers.some((u) => !isBlocked(u))}
              className="ul-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium disabled:opacity-40"
            >
              <FaBan size={11} /> Block
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ type: "bulk-unblock", users: selectedUsers.filter(isBlocked) })}
              disabled={!selectedUsers.some(isBlocked)}
              className="ul-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-40"
            >
              <FaUnlock size={11} /> Unblock
            </button>
            <button
              type="button"
              onClick={() => exportCsv(selectedUsers)}
              className="ul-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200 font-medium"
            >
              <FaFileCsv size={11} /> Export
            </button>
            <button type="button" onClick={() => setSelectedIds([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
              Clear selection
            </button>
          </div>
        ) : (
          hasFilters && (
            <div className="flex items-center gap-2 px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
              <span className="text-slate-600 dark:text-slate-300">
                Showing {fmt(filtered.length)} of {fmt(users.length)} users
              </span>
              <button type="button" onClick={clearFilters} className="ml-auto text-cyan-700 dark:text-cyan-400 font-medium hover:underline">
                Clear filters
              </button>
            </div>
          )
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="pl-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={togglePageSelect}
                    className="w-4 h-4 accent-cyan-600 cursor-pointer"
                    aria-label="Select all on this page"
                  />
                </th>
                <SortTh field="name">User</SortTh>
                <th className="px-3 py-3 font-medium text-left">Contact</th>
                <SortTh field="gender">Gender</SortTh>
                <th className="px-3 py-3 font-medium text-left">DOB</th>
                <SortTh field="point" right>Points</SortTh>
                <SortTh field="joined">Joined</SortTh>
                <SortTh field="status">Status</SortTh>
                <th className="px-3 py-3 pr-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {loading && !users.length &&
                [0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <tr key={i}>
                    <td colSpan={9} className="px-4 py-3">
                      <div className="h-9 rounded-md bg-slate-100 dark:bg-slate-700 animate-pulse" />
                    </td>
                  </tr>
                ))}

              {!loading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-14 text-center">
                    <FaInbox className="mx-auto text-slate-300" size={32} />
                    <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">
                      {users.length ? "No users match these filters" : "No users found"}
                    </p>
                    {hasFilters && (
                      <button type="button" onClick={clearFilters} className="mt-2 text-sm text-cyan-700 font-medium hover:underline">
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              )}

              {pageRows.map((u) => {
                const d = joinedAt(u);
                const sel = selectedIds.includes(u.user_id);
                const open = drawerUser?.user_id === u.user_id;
                return (
                  <tr
                    key={u.user_id}
                    onClick={() => setDrawerUser(u)}
                    className={`ul-row ${
                      open
                        ? "bg-cyan-50/70 dark:bg-cyan-900/20"
                        : sel
                        ? "bg-slate-50 dark:bg-slate-800/40"
                        : "hover:bg-slate-50 dark:hover:bg-slate-700/40"
                    } ${isBlocked(u) ? "opacity-80" : ""}`}
                  >
                    <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => toggleSelect(u.user_id)}
                        className="w-4 h-4 accent-cyan-600 cursor-pointer"
                        aria-label={`Select ${u.name}`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-3 min-w-[180px]">
                        <Avatar src={u.profile_img} name={u.name} />
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 dark:text-white truncate max-w-[160px]">{u.name || "No name"}</p>
                          <p className="text-xs text-slate-500 tabular-nums">{u.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-slate-700 dark:text-slate-200 truncate max-w-[220px]" title={u.email_id}>
                        {u.email_id || <span className="text-slate-400">-</span>}
                      </p>
                      <p className="text-xs text-slate-500 tabular-nums">{u.contact || "-"}</p>
                    </td>
                    <td className="px-3 py-3 text-slate-700 dark:text-slate-200">{u.gender || <span className="text-slate-400">-</span>}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-700 dark:text-slate-200">{u.dob || <span className="text-slate-400">-</span>}</td>
                    <td className="px-3 py-3 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{fmt(u.point)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                      <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge user={u} />
                    </td>
                    <td className="px-3 py-3 pr-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => navigate(`/user-profile/${u.user_id}`)}
                          title="Open full profile"
                          aria-label="Open full profile"
                          className="ul-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          <FaExternalLinkAlt size={11} />
                        </button>
                        <button
                          type="button"
                          onClick={() => askToggle(u)}
                          className={`ul-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm font-medium whitespace-nowrap ${
                            isBlocked(u)
                              ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                              : "bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
                          }`}
                        >
                          {isBlocked(u) ? <FaUnlock size={11} /> : <FaBan size={11} />}
                          {isBlocked(u) ? "Unblock" : "Block"}
                        </button>
                        <button
                          type="button"
                          onClick={() => askDelete(u)}
                          title="Delete user"
                          aria-label="Delete user"
                          className="ul-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-500 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              Rows per page
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={`${INPUT} h-8 px-2`}>
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-600 dark:text-slate-300 tabular-nums">
                {fmt((safePage - 1) * pageSize + 1)}–{fmt(Math.min(safePage * pageSize, filtered.length))} of {fmt(filtered.length)}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="ul-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40"
                  aria-label="Previous page"
                >
                  <FaChevronLeft size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="ul-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40"
                  aria-label="Next page"
                >
                  <FaChevronRight size={11} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ================= Quick view drawer ================= */}
      {drawerUser && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="ul-fade absolute inset-0 bg-slate-900/40" onClick={() => setDrawerUser(null)} />
          <aside className="ul-slide relative w-full max-w-md h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <p className="text-sm font-medium text-slate-500">User details</p>
              <button
                type="button"
                onClick={() => setDrawerUser(null)}
                className="ul-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              <div className="flex flex-col items-center text-center">
                <Avatar src={drawerUser.profile_img} name={drawerUser.name} size={84} />
                <h2 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">{drawerUser.name || "No name"}</h2>
                <button
                  type="button"
                  onClick={() => copyText(drawerUser.user_id, "User ID copied")}
                  className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 tabular-nums"
                >
                  ID {drawerUser.user_id} <FaCopy size={10} />
                </button>
                <div className="mt-2">
                  <StatusBadge user={drawerUser} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                  <p className="flex items-center gap-1.5 text-xs text-slate-500"><FaCoins className="text-amber-500" /> Points</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{fmt(drawerUser.point)}</p>
                </div>
                <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                  <p className="text-xs text-slate-500">Joined</p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{fmtDate(joinedAt(drawerUser))}</p>
                  <p className="text-xs text-slate-500">{fmtTime(joinedAt(drawerUser))}</p>
                </div>
              </div>

              <section className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                {[
                  { icon: FaEnvelope, label: "Email", value: drawerUser.email_id, href: drawerUser.email_id ? `mailto:${drawerUser.email_id}` : null },
                  { icon: FaPhoneAlt, label: "Contact", value: drawerUser.contact, href: drawerUser.contact ? `tel:${drawerUser.contact}` : null },
                ].map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.label} className="flex items-center gap-3 px-4 py-3">
                      <Icon className="text-slate-400 shrink-0" size={13} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500">{r.label}</p>
                        {r.href ? (
                          <a href={r.href} className="text-sm text-slate-900 dark:text-white hover:underline break-all">{r.value}</a>
                        ) : (
                          <p className="text-sm text-slate-400">-</p>
                        )}
                      </div>
                      {r.value && (
                        <button
                          type="button"
                          onClick={() => copyText(r.value)}
                          className="p-1.5 text-slate-400 hover:text-slate-700"
                          aria-label={`Copy ${r.label}`}
                        >
                          <FaCopy size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
                <div className="grid grid-cols-2 divide-x divide-slate-100 dark:divide-slate-700">
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500">Gender</p>
                    <p className="text-sm text-slate-900 dark:text-white">{drawerUser.gender || "-"}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500">DOB</p>
                    <p className="text-sm text-slate-900 dark:text-white">{drawerUser.dob || "-"}</p>
                  </div>
                </div>
              </section>

              {waLink(drawerUser.contact) && (
                <a
                  href={waLink(drawerUser.contact)}
                  target="_blank"
                  rel="noreferrer"
                  className="ul-press flex items-center justify-center gap-2 h-10 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-sm font-semibold"
                >
                  <FaWhatsapp size={16} /> Message on WhatsApp
                </a>
              )}

              <button
                type="button"
                onClick={() => navigate(`/user-profile/${drawerUser.user_id}`)}
                className="ul-press w-full flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <FaExternalLinkAlt size={11} /> Open full profile
              </button>
            </div>

            <div className="flex gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <button
                type="button"
                onClick={() => askDelete(drawerUser)}
                className="ul-press h-10 px-4 rounded-lg border border-rose-200 dark:border-rose-800 bg-white dark:bg-secondary-dark-bg text-rose-600 text-sm font-semibold inline-flex items-center gap-2"
              >
                <FaTrash size={11} /> Delete
              </button>
              <button
                type="button"
                onClick={() => askToggle(drawerUser)}
                className={`ul-press flex-1 h-10 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 ${
                  isBlocked(drawerUser) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"
                }`}
              >
                {isBlocked(drawerUser) ? <FaUnlock size={12} /> : <FaBan size={12} />}
                {isBlocked(drawerUser) ? "Unblock user" : "Block user"}
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Confirm dialog ================= */}
      {confirm && confirm.users.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="ul-fade absolute inset-0 bg-slate-900/50" onClick={() => !working && setConfirm(null)} />
          <div className="ul-zoom relative w-full max-w-md rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span
              className={`w-11 h-11 rounded-full flex items-center justify-center ${
                confirm.type.includes("unblock")
                  ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40"
                  : "bg-rose-100 text-rose-600 dark:bg-rose-900/40"
              }`}
            >
              {confirm.type === "delete" ? <FaExclamationTriangle size={18} /> : ct.icon && <ct.icon size={18} />}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{ct.title}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{ct.body}</p>

            {confirm.users.length === 1 && (
              <div className="mt-4 flex items-center gap-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                <Avatar src={confirm.users[0].profile_img} name={confirm.users[0].name} size={36} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{confirm.users[0].name || "No name"}</p>
                  <p className="text-xs text-slate-500 tabular-nums">{confirm.users[0].user_id}</p>
                </div>
              </div>
            )}

            {confirm.type === "delete" && (
              <div className="mt-4">
                <label htmlFor="delete-confirm" className="text-sm text-slate-700 dark:text-slate-200">
                  Type <b>DELETE</b> to confirm
                </label>
                <input
                  id="delete-confirm"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  autoFocus
                  className={`${INPUT} w-full h-10 px-3 mt-1.5`}
                  placeholder="DELETE"
                />
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={working}
                className="ul-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working || !deleteReady}
                className={`ul-press flex-1 h-10 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${ct.color}`}
              >
                {working && <FaSyncAlt className="animate-spin" size={12} />}
                {working ? "Please wait..." : ct.button}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;